import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Loader2, Bot, TrendingUp, Users, Zap, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      login(data.token, data.user);
      toast.success('Welcome to AI Marketing Automation!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const metrics = [
    { icon: DollarSign, value: '$78,346', change: '+275%', label: 'Total Revenue' },
    { icon: Users, value: '18.7K', change: '+156%', label: 'New Leads' },
    { icon: TrendingUp, value: '89.2%', change: '+42%', label: 'Engagement' },
    { icon: Zap, value: '2.4x', change: '+85%', label: 'Growth' },
  ];

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 ai-gradient items-center justify-center p-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-white/20 blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl"></div>
        </div>
        
        <div className="max-w-md text-gray-900 dark:text-white relative z-10">
          <div className="w-20 h-20  backdrop-blur rounded-2xl flex items-center justify-center mb-8">
            {/* <Bot className="w-8 h-8" /> */}
            <img
              src="/logoai1.png"
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold mb-4">AI Marketing Automation</h1>
          <p className="text-gray-700 dark:text-white/80 text-lg leading-relaxed mb-8">
            Automate. Grow. Succeed. Your AI-powered marketing companion that handles campaigns, content, and conversions.
          </p>
          
          {/* Metric Cards */}
          <div className="grid grid-cols-2 gap-4">
            {metrics.map((metric, index) => (
              <div
  key={index}
  className="rounded-xl p-4 bg-white/80 dark:bg-white/10 backdrop-blur border border-gray-200 dark:border-white/10"
>
                <div className="flex items-center gap-2 mb-2">
                  <metric.icon className="w-4 h-4 text-gray-600 dark:text-white/60" />
                  <span className="text-xs text-gray-600 dark:text-white/60">{metric.label}</span>
                </div>
                <p className="text-xl font-bold">{metric.value}</p>
                <span className="text-xs text-emerald-300">{metric.change}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-0 mb-6">
  
  <div className="w-20 h-20 flex items-center justify-center shrink-0">
    <img
      src="/logoai.png"
      alt="Logo"
      className="w-full h-full object-contain scale-110"
    />
  </div>

  <div className="-ml-1 leading-none">
    <span className="text-lg font-bold block">
      AI Marketing
    </span>

    <p className="text-[11px] text-muted-foreground mt-1">
      Automation
    </p>
  </div>

</div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-2">Welcome Back!</h2>
            <p className="text-muted-foreground">Sign in to manage your marketing campaigns</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="marketing-input"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="marketing-input"
                data-testid="login-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="marketing-btn-primary w-full h-12 flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Use your AISalesTask CRM credentials to login
          </p>
        </div>
      </div>
    </div>
  );
}
